{
  "title": "如何管理活動成員與主辦方後台",
  "description": "新增活動成員與角色，並使用主辦方後台的總覽、公告與 Email 推播、服務台、餐飲匯出與營運工具。",
  "headings": [
    "你會完成什麼",
    "適用角色",
    "操作前確認",
    "操作步驟",
    "完成後",
    "常見問題"
  ],
  "summary": "完成後，活動工作人員各有最小必要的權限，主辦方能發布公告、處理客服案件、管理志工與海報，並在現場掌握接駁預約與驗票數。",
  "roleNote": "Owner（含平台超級管理員）可做全部；Manager 可處理公告、Email 推播、餐飲匯出與服務單；Scanner 驗票與名單；Viewer 只看報表",
  "prerequisites": [
    "您是該活動的 Owner，或為平台超級管理員",
    "要加入的成員 Email（可先加入，對方註冊並驗證 Email 後才生效）"
  ],
  "pathLabel": "帳號選單 → 活動管理 → 該活動，或 /events/:slug/admin；營運工具在 /events/:slug/admin/operations",
  "steps": [
    {
      "title": "1. 進入主辦方後台",
      "description": "有活動工作人員身分者，右上角帳號選單會出現「活動管理」，點入該活動的後台（多個活動時每個活動一項，最多 5 項）。後台依您的能力分成分頁：總覽、公告、服務台、成員；只有一個分頁可用時不顯示分頁列，目前分頁會記在網址參數 tab。"
    },
    {
      "title": "2. 新增成員與指定角色",
      "description": "到「成員」分頁輸入 Email 並選角色：Owner（全部功能含成員管理）、Manager（公告、Email 推播、餐飲匯出、服務單）、Scanner（驗票與乘客名單）、Viewer（僅檢視報表）。Email 會轉為小寫並去重，單一活動最多 50 位成員。對方尚未註冊時顯示「待註冊」，註冊並驗證 Email 後自動變為「已生效」。可改角色或移除；最後一位 Owner 不能被移除或降級，要先新增另一位 Owner。所有變更都有稽核紀錄。"
    },
    {
      "title": "3. 看總覽與發布公告",
      "description": "「總覽」顯示 KPI（已預約座位、候補、已驗票、班次數）、各時段容量與餐飲、宴會回覆彙總、未結案件數，並標示資料來源與更新時間。「公告」分頁可發布中英文雙語公告（一般、重要、緊急）並可撤回；發布與撤回都會寫入稽核。"
    },
    {
      "title": "4. 處理服務台案件",
      "description": "「服務台」列出所有案件，可把狀態改為新案件、處理中、已解決或已結案。需要 Email 通知持有接駁預約者時，到營運管理的「公告 Email」分頁：先「預覽收件對象」看人數與遮罩樣本，確認後才按「確認並寄出」。每位收件人每則公告只會收到一次，單次上限 500 位，寄出後無法撤回。"
    },
    {
      "title": "5. 營運工具（僅 Owner）",
      "description": "在 /events/:slug/admin/operations：志工班表（新增班次並以 Email 指派志工，志工只看得到自己的班次）、海報與設備（匯入官方配置，每行「位置代碼, 標題, 發表者 Email」，位置重複則整批不寫入；核准或拒絕設備申請）、講者檔案（加入講者名單；只收 PDF 與 PPTX，30 MB 以內，一律先隔離，請下載後以本機防毒軟體檢查再核准）。"
    },
    {
      "title": "6. 餐飲與緊急聯絡資料",
      "description": "餐飲只在儀表板顯示各飲食類別人數；Owner 與 Manager 可匯出明細，每次匯出寫入稽核。乘車聯絡電話只有 Owner 能在乘客名單載入，每次載入留紀錄。餐飲、聯絡資料與提醒紀錄在活動結束後 30 天自動刪除。"
    }
  ],
  "afterward": [
    "成員、公告、案件狀態與匯出都有稽核紀錄可查",
    "活動頁依成員身分只對有能力者顯示驗票、名單與後台入口（真正的授權仍在伺服器）"
  ],
  "nextSteps": "現場驗票請見「如何在活動現場驗票與核對乘客名單」。",
  "warning": "最後一位 Owner 受資料庫保護，不能被移除或降級。寄出公告 Email 後無法撤回，請務必先預覽收件對象。",
  "faq": [
    {
      "q": "Manager 可以驗票嗎？",
      "a": "不行。驗票與乘客名單屬於 Scanner 與 Owner；Manager 負責公告、Email 推播、餐飲匯出與服務單。"
    },
    {
      "q": "我是競賽主辦方，會自動是活動 Owner 嗎？",
      "a": "不會。活動成員與競賽權限是分開的；只有被加入該活動的成員與平台超級管理員才有活動權限。"
    },
    {
      "q": "為什麼看不到「活動管理」？",
      "a": "只有在至少一個已發布活動具工作人員身分的使用者才會看到；請向活動 Owner 確認已用已驗證的 Email 加入。"
    }
  ],
  "figures": [
    {
      "name": "admin-overview",
      "alt": "主辦方後台總覽：KPI、各班次容量與餐飲彙總，並標示資料來源與更新時間",
      "caption": "主辦方後台總覽：KPI、各班次容量與餐飲彙總，並標示資料來源與更新時間"
    },
    {
      "name": "admin-members",
      "alt": "成員分頁：新增成員、指定角色，尚未註冊者顯示為待註冊",
      "caption": "成員分頁：新增成員、指定角色，尚未註冊者顯示為待註冊"
    }
  ]
}
